// RecipeManager.jsx — Container-free, Notes as array, Notes then Tags at the bottom.
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Switch, FormControlLabel,
  Accordion, AccordionSummary, AccordionDetails, Dialog, DialogTitle,
  DialogContent, DialogActions, Snackbar, Alert, Stack, Tabs, Tab, Divider, Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Form from '@rjsf/mui';
import validator from '@rjsf/validator-ajv8';

import { db } from './firebase';
import { collection, getDocs, doc, updateDoc, deleteDoc, addDoc, query, where } from 'firebase/firestore';

const G_PER_OZ = 28.349523125;

// ---------- utils ----------
const deepClone = (obj) => JSON.parse(JSON.stringify(obj));
const uniq = (arr) => Array.from(new Set((arr || []).filter(v => v !== undefined && v !== null)));
const toLowerId = (v) => (v == null ? '' : String(v).trim().toLowerCase());
const stripUndefinedDeep = (val) => {
  if (Array.isArray(val)) return val.map(stripUndefinedDeep).filter((x) => x !== undefined);
  if (val && typeof val === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(val)) {
      const cleaned = stripUndefinedDeep(v);
      if (cleaned !== undefined) out[k] = cleaned;
    }
    return out;
  }
  return val === undefined ? undefined : val;
};
const pruneToSchema = (data, s) => {
  if (!s?.properties || !data) return data;
  const allowed = new Set(Object.keys(s.properties));
  const out = {};
  for (const [k, v] of Object.entries(data)) if (allowed.has(k)) out[k] = v;
  return out;
};

// ---------- custom widgets ----------
const ReferenceWidget = (props) => {
  const { formContext, id, value, onChange, onBlur, onFocus, required, disabled: uiDisabled, readonly } = props;
  const fd = formContext?.formData || {};
  let auto = false;
  const m = id.match(/^root_servings_(\d+)_ingredients_(\d+)_reference$/);
  if (m) {
    const [sIdx, iIdx] = [Number(m[1]), Number(m[2])];
    auto = !!fd?.servings?.[sIdx]?.ingredients?.[iIdx]?._autoGeneratedRef;
  }
  const isDisabled = uiDisabled || readonly || auto;

  return (
    <TextField
      fullWidth
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      onBlur={(e) => onBlur && onBlur(id, e.target.value)}
      onFocus={(e) => onFocus && onFocus(id, e.target.value)}
      required={required}
      disabled={isDisabled}
      size="small"
      placeholder="ref"
    />
  );
};

// ---------- component ----------
export default function RecipeManager({ schema, defaultRecipeType, uiSchema: uiSchemaProp = {}, widgets: widgetsProp = {} }) {
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sizing, setSizing] = useState([]);
  const [flavors, setFlavors] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [units, setUnits] = useState([]);
  const [tools, setTools] = useState([]);
  const [schemaReady, setSchemaReady] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);

  const [formData, setFormData] = useState(null);
  const [editingDocId, setEditingDocId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [tab, setTab] = useState(0);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [archiveTarget, setArchiveTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // maps
  const unitMap = useMemo(() => Object.fromEntries(units.map(u => [toLowerId(u.id), u])), [units]);
  const ingredientMap = useMemo(() => Object.fromEntries(ingredients.map(i => [i.id, i])), [ingredients]);
  const sizeLabel = (id) => (sizing.find(sz => sz.id === id)?.label) || id || '';
  const toolNameById = (id) => (tools.find(t => String(t.id).trim() === String(id).trim())?.name) || String(id).replaceAll('_', ' ');

  // data fetch
  const loadRecipes = async () => {
    const baseRef = collection(db, 'drink_recipes');
    const recipesSnap = defaultRecipeType
      ? await getDocs(query(baseRef, where('recipe_type', '==', defaultRecipeType)))
      : await getDocs(baseRef);
    setRecipes(recipesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    const fetchData = async () => {
      const baseRef = collection(db, 'drink_recipes');
      const recipeQuery = defaultRecipeType ? query(baseRef, where('recipe_type', '==', defaultRecipeType)) : baseRef;

      const [catSnap, recipeSnap, sizeSnap, flavorSnap, ingredientSnap, unitSnap, toolSnap] = await Promise.all([
        getDocs(collection(db, 'recipe_categories')),
        getDocs(recipeQuery),
        getDocs(collection(db, 'beverage_sizes')),
        getDocs(collection(db, 'recipe_flavors')),
        getDocs(collection(db, 'recipe_ingredients')),
        getDocs(collection(db, 'ingredient_units')),
        getDocs(collection(db, 'equipment_tools'))
      ]);

      setCategories(catSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setRecipes(recipeSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setSizing(sizeSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setFlavors(flavorSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setIngredients(ingredientSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setUnits(unitSnap.docs.map(d => ({ id: String(d.id).trim(), ...d.data() })));
      setTools(toolSnap.docs.map(d => ({ id: String(d.id).trim(), ...d.data() })));
      setSchemaReady(true);
    };
    fetchData();
  }, [defaultRecipeType]);

  // auto refs
  const indexToLetters = (n) => { let s = ''; n = Number(n); while (n >= 0) { s = String.fromCharCode((n % 26) + 97) + s; n = Math.floor(n / 26) - 1; } return s; };
  const fillRefsForArray = (arr) => {
    if (!Array.isArray(arr)) return arr;
    const used = new Set(arr.map(i => (i?.reference || '').toString().trim().toLowerCase()).filter(Boolean));
    let cursor = 0;
    const nextRef = () => { let c; do { c = indexToLetters(cursor++); } while (used.has(c)); used.add(c); return c; };
    return arr.map((ing) => {
      if (!ing) return ing;
      const auto = ing._autoGeneratedRef !== false; // default true
      const current = (ing.reference ?? '').toString().trim();
      if (!auto) return { ...ing, reference: current.toLowerCase() };
      if (!current) return { ...ing, reference: nextRef(), _autoGeneratedRef: true };
      const norm = current.toLowerCase();
      if (!used.has(norm)) used.add(norm);
      return { ...ing, reference: norm, _autoGeneratedRef: true };
    });
  };

  // default role helper
  const defaultRoleForTool = (id) => {
    const k = String(id || '').toLowerCase();
    if (k === 'shaker') return 'mix';
    if (k === 'blender_jar') return 'blend';
    if (k === 'serving_cup' || k === 'cup') return 'serve';
    return undefined;
  };

  // ensure equipment
  const ensureAutoEquipment = (fd) => {
    if (!fd) return fd;
    const out = { ...fd };
    if (Array.isArray(out.servings)) {
      out.servings = out.servings.map(s => {
        if (!s) return s;
        const autoId = `${s.size || ''}_${(s.temperature || '').toLowerCase()}`.replace(/\s+/g, '');
        let equipment = Array.isArray(s.equipment) ? s.equipment.filter(Boolean) : [];
        equipment = equipment.map(e => (e?.role && e.role !== 'other') ? e : { ...e, role: defaultRoleForTool(e.tool_id) || e.role });
        if (!equipment.length) {
          const def = (s.temperature || '').toLowerCase() === 'blended' ? 'blender_jar' : 'shaker';
          equipment = [{ tool_id: def, count: 1, role: defaultRoleForTool(def) }];
        }
        return { ...s, id: autoId, equipment, ingredients: fillRefsForArray(s.ingredients || []) };
      });
    }
    return out;
  };

  // Notes back-compat: string → array
  const coerceNotes = (fd) => {
    if (!fd) return fd;
    const out = { ...fd };
    const v = out.notes;
    if (Array.isArray(v)) {
      out.notes = v.filter(x => typeof x === 'string' && x.trim() !== '');
    } else if (typeof v === 'string') {
      const t = v.trim();
      out.notes = t ? [t] : [];
    } else {
      out.notes = [];
    }
    return out;
  };

  // units coercion
  const coerceLegacyUnits = (fd) => {
    if (!fd?.servings?.length || !units?.length) return { data: fd, unmatched: [] };
    const validIds = new Set(units.map(u => toLowerId(u.id)));
    const labelToId = new Map(units.map(u => [toLowerId(u.label || ''), u.id]));
    const unmatchedSet = new Set();
    const coerce = (val) => {
      if (!val) return val;
      const raw = String(val).trim(); const lower = raw.toLowerCase();
      if (validIds.has(lower)) return units.find(x => toLowerId(x.id) === lower)?.id || raw;
      if (labelToId.has(lower)) return labelToId.get(lower);
      if (/\boz\b|ounce/.test(lower) && validIds.has('oz')) return 'oz';
      if (/\bml\b|milliliter|millilitre/.test(lower) && validIds.has('ml')) return 'ml';
      if (/\bg\b|gram/.test(lower) && validIds.has('g')) return 'g';
      unmatchedSet.add(raw); return raw;
    };
    const out = deepClone(fd);
    out.servings = (out.servings || []).map(s => ({
      ...s,
      ingredients: (s.ingredients || []).map(row => (!row ? row : { ...row, unit_id: coerce(row.unit_id) }))
    }));
    return { data: out, unmatched: Array.from(unmatchedSet) };
  };

  // computed quantities
  const gramsPerUnit = (ingredient_id, unit_id) => {
    const ing = ingredientMap[ingredient_id];
    const cal = ing?.unit_calibrations?.find(c => toLowerId(c.unit_id) === toLowerId(unit_id));
    if (cal?.grams_per_unit > 0) return cal.grams_per_unit;
    if (toLowerId(unit_id) === 'g') return 1;
    if (toLowerId(unit_id) === 'oz') return G_PER_OZ;
    return undefined;
  };
  const computeQuantities = (fd) => {
    if (!fd?.servings) return fd;
    const next = { ...fd, servings: fd.servings.map(s => ({ ...s })) };
    next.servings.forEach((serv) => {
      if (!Array.isArray(serv.ingredients)) return;
      serv.ingredients = serv.ingredients.map((row) => {
        if (!row) return row;
        const gpu = gramsPerUnit(row.ingredient_id, row.unit_id);
        let computed_grams, computed_oz;
        if (typeof gpu === 'number') {
          computed_grams = Number(row.qty || 0) * gpu;
          computed_oz = computed_grams / G_PER_OZ;
        }
        return {
          ...row,
          computed_grams: typeof computed_grams === 'number' ? parseFloat(computed_grams.toFixed(2)) : undefined,
          computed_oz: typeof computed_oz === 'number' ? parseFloat(computed_oz.toFixed(2)) : undefined
        };
      });
    });
    return next;
  };

  // unit issues
  const validUnitIds = useMemo(() => new Set(units.map(u => toLowerId(u.id))), [units]);
  const recipeUnitIssues = useMemo(() => {
    const issues = {};
    const getGpu = (ingredient_id, unit_id) => {
      const ing = ingredientMap[ingredient_id];
      const cal = ing?.unit_calibrations?.find(c => toLowerId(c.unit_id) === toLowerId(unit_id));
      if (cal?.grams_per_unit > 0) return cal.grams_per_unit;
      if (toLowerId(unit_id) === 'g') return 1;
      if (toLowerId(unit_id) === 'oz') return G_PER_OZ;
      return undefined;
    };
    for (const r of recipes) {
      let invalid = 0, uncalibrated = 0;
      (r?.servings || []).forEach(s => (s?.ingredients || []).forEach(row => {
        const uid = toLowerId(row?.unit_id);
        if (!uid || !validUnitIds.has(uid)) invalid++;
        else if (getGpu(row?.ingredient_id, uid) === undefined) uncalibrated++;
      }));
      issues[r.id] = { invalid, uncalibrated, total: invalid + uncalibrated };
    }
    return issues;
  }, [recipes, validUnitIds, ingredientMap]);

  const [showUnitIssuesOnly, setShowUnitIssuesOnly] = useState(false);

  // dynamic schema (inject enums & live refs)
  const formSchema = useMemo(() => {
    if (!schemaReady || !schema) return schema;
    const s = deepClone(schema);

    if (s.properties?.recipe_type) {
      s.properties.recipe_type.default = defaultRecipeType || s.properties.recipe_type.default;
      s.properties.recipe_type.type = 'string';
    }

    if (s.properties?.category) {
      s.properties.category.enum = uniq(categories.map(c => c.id));
      s.properties.category.enumNames = uniq(categories.map(c => c.name));
    }
    if (s.properties?.supported_flavors?.items) {
      s.properties.supported_flavors.items.enum = uniq(flavors.map(f => f.id));
      s.properties.supported_flavors.items.enumNames = uniq(flavors.map(f => f.name));
    }

    if (s.definitions?.serving?.properties?.size) {
      s.definitions.serving.properties.size.enum = uniq(sizing.map(sz => sz.id));
      s.definitions.serving.properties.size.enumNames = uniq(sizing.map(sz => sz.label));
    }

    const sip = s.definitions?.serving_ingredient?.properties;
    if (sip) {
      const ingIds = [], ingNames = [], seenIng = new Set();
      ingredients.forEach(i => { if (!seenIng.has(i.id)) { seenIng.add(i.id); ingIds.push(i.id); ingNames.push(i.name); } });
      sip.ingredient_id.enum = ingIds; sip.ingredient_id.enumNames = ingNames;

      const unitIds = [], unitNames = [], seenUnit = new Set();
      units.forEach(u => { const id = String(u.id).trim(); if (!seenUnit.has(id)) { seenUnit.add(id); unitIds.push(id); unitNames.push(u.label || id); } });

      (formData?.servings || []).forEach(srv => (srv?.ingredients || []).forEach(row => {
        const id = String(row?.unit_id ?? '').trim();
        if (id && !seenUnit.has(id)) { seenUnit.add(id); unitIds.push(id); unitNames.push(id); }
      }));

      sip.unit_id.enum = unitIds; sip.unit_id.enumNames = unitNames;
    }

    // tool dropdowns
    {
      const prop = s.definitions?.equipment_item?.properties?.tool_id;
      if (prop) {
        const recipeType = String(defaultRecipeType || 'beverage').toLowerCase();
        const seen = new Set(); const ids = []; const names = [];
        tools
          .filter(t => {
            const list = Array.isArray(t.types) ? t.types.map(x => String(x).toLowerCase()) : null;
            return !list || list.length === 0 || list.includes(recipeType);
          })
          .forEach(t => {
            const id = String(t.id).trim();
            if (!seen.has(id)) { seen.add(id); ids.push(id); names.push(t.name || id); }
          });
        prop.enum = ids; prop.enumNames = names;
      }
    }

    // step fields (no container_ref)
    s.definitions = s.definitions || {};
    s.definitions.step = s.definitions.step || { type: 'object', properties: {} };
    s.definitions.step.properties = s.definitions.step.properties || {};
    const stepProps = s.definitions.step.properties;

    if (!stepProps.applies_to_servings) stepProps.applies_to_servings = { type: 'array', items: { type: 'string' }, uniqueItems: true };
    if (!stepProps.ingredient_refs) stepProps.ingredient_refs = { type: 'array', items: { type: 'string' }, uniqueItems: true };
    if (!stepProps.equipment_refs) stepProps.equipment_refs = { type: 'array', items: { type: 'string' }, uniqueItems: true };

    // dynamic enums from current form state
    const current = ensureAutoEquipment(formData || {});
    const sv = Array.isArray(current?.servings) ? current.servings : [];

    // applies_to_servings
    {
      const ids = sv.map(srv => (srv?.id || `${srv?.size || ''}_${String(srv?.temperature || '').toLowerCase()}`.replace(/\s+/g, ''))).filter(Boolean);
      const names = sv.map(srv => `${sizeLabel(srv?.size)} ${srv?.temperature || ''}`.trim());
      stepProps.applies_to_servings.items.type = 'string';
      if (ids.length > 0) {
        stepProps.applies_to_servings.items.enum = ids;
        stepProps.applies_to_servings.items.enumNames = names;
      } else {
        delete stepProps.applies_to_servings.items.enum;
        delete stepProps.applies_to_servings.items.enumNames;
      }
    }

    // ingredient_refs
    {
      const refMap = new Map();
      for (const srv of sv) {
        for (const row of (srv?.ingredients || [])) {
          const ref = (row?.reference || '').toString().trim().toLowerCase();
          if (!ref) continue;
          if (!refMap.has(ref)) {
            const ingName = ingredientMap[row.ingredient_id]?.name || row.ingredient_id || '';
            refMap.set(ref, ingName ? `${ref} — ${ingName}` : ref);
          }
        }
      }
      stepProps.ingredient_refs.items.type = 'string';
      const keys = Array.from(refMap.keys());
      const vals = Array.from(refMap.values());
      if (keys.length > 0) {
        stepProps.ingredient_refs.items.enum = keys; stepProps.ingredient_refs.items.enumNames = vals;
      } else {
        delete stepProps.ingredient_refs.items.enum; delete stepProps.ingredient_refs.items.enumNames;
      }
    }

    // equipment_refs
    {
      const pairMap = new Map();
      for (const srv of sv) {
        for (const eq of (srv?.equipment || [])) {
          const key = eq?.ref || eq?.tool_id;
          if (!key) continue;
          const label = eq?.ref ? `${eq.ref} — ${toolNameById(eq.tool_id)}` : toolNameById(eq.tool_id);
          if (!pairMap.has(key)) pairMap.set(key, label);
        }
      }
      stepProps.equipment_refs.items.type = 'string';
      if (pairMap.size > 0) {
        stepProps.equipment_refs.items.enum = Array.from(pairMap.keys());
        stepProps.equipment_refs.items.enumNames = Array.from(pairMap.values());
      } else {
        delete stepProps.equipment_refs.items.enum; delete stepProps.equipment_refs.items.enumNames;
      }
    }

    return s;
  }, [schemaReady, schema, categories, sizing, flavors, ingredients, units, tools, defaultRecipeType, formData]);

  // crew preview
  const ucFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
  const pluralize = (label, qty) => (!label ? label : Math.abs(qty) === 1 ? label : (label.endsWith('s') ? label : `${label}s`));
  const resolveCrewLines = (fd) => {
    if (!fd || !Array.isArray(fd.servings)) return {};
    const result = {};
    for (const serv of fd.servings) {
      if (!serv) continue;
      const id = serv.id || `${serv.size || ''}_${(serv.temperature || '').toLowerCase()}`;
      const ingByRef = {};
      for (const row of (serv.ingredients || [])) {
        if (!row) continue;
        const ref = (row.reference || '').toString().toLowerCase();
        if (!ref) continue;
        const ingMeta = ingredientMap[row.ingredient_id];
        const unitMeta = unitMap[toLowerId(row.unit_id)];
        ingByRef[ref] = { qty: row.qty, unitLabel: unitMeta?.label || row.unit_id, ingredientName: ingMeta?.name || row.ingredient_id };
      }
      const lines = [];
      for (const step of (fd.steps || [])) {
        if (Array.isArray(step.applies_to_servings) && step.applies_to_servings.length) {
          if (!step.applies_to_servings.includes(serv.id)) continue;
        }
        const refs = (step.ingredient_refs || []).map(r => r.toString().toLowerCase());
        const parts = [];
        for (const r of refs) {
          const it = ingByRef[r];
          if (!it) continue;
          const unitPhrase = pluralize(it.unitLabel, it.qty);
          parts.push(`${it.qty} ${unitPhrase} ${it.ingredientName}`);
        }
        const list = parts.length > 1 ? parts.slice(0, -1).join(', ') + ' and ' + parts.slice(-1) : (parts[0] || '');

        let equipPhrase = '';
        const eqList = Array.isArray(serv.equipment) ? serv.equipment : [];
        if (Array.isArray(step.equipment_refs) && step.equipment_refs.length) {
          const match = eqList.filter(eq => step.equipment_refs.includes(eq.ref) || step.equipment_refs.includes(eq.tool_id));
          if (match.length) {
            const eqParts = match.map(eq => `${eq.count} ${pluralize((tools.find(t => String(t.id).trim() === String(eq.tool_id).trim())?.name || eq.tool_id).toLowerCase(), eq.count)}`);
            equipPhrase = ` to ${eqParts.join(' and ')}`;
          }
        }

        const base = (step.instruction || '').trim().replace(/\.\s*$/, '');
        const line = list ? `${ucFirst(base.split(' ')[0] || 'Add')} ${list}${equipPhrase}.` : `${ucFirst(base)}${equipPhrase}.`;
        lines.push(line);
      }
      result[id || 'serving'] = lines;
    }
    return result;
  };

  // CRUD
  const handleSubmit = async (data) => {
    let payload = coerceNotes(ensureAutoEquipment(data));
    if (defaultRecipeType) payload.recipe_type = defaultRecipeType;
    payload = computeQuantities(payload);

    if (Array.isArray(payload.servings)) {
      payload.servings = payload.servings.map(({ container, ...s }) => ({
        ...s,
        ingredients: Array.isArray(s.ingredients) ? s.ingredients.map(row => {
          const { _autoGeneratedRef, ...rest } = row || {};
          return rest;
        }) : s.ingredients
      }));
    }

    payload.schema_version = 4; // keep upgraded
    payload = stripUndefinedDeep(payload);

    if (editingDocId) {
      await updateDoc(doc(db, 'drink_recipes', editingDocId), payload);
      setSnackbar({ open: true, message: 'Recipe updated.', severity: 'success' });
    } else {
      await addDoc(collection(db, 'drink_recipes'), payload);
      setSnackbar({ open: true, message: 'Recipe added.', severity: 'success' });
    }
    setShowForm(false);
    setEditingDocId(null);
    await loadRecipes();
  };

  const doArchive = async (recipe) => { if (!recipe) return; await updateDoc(doc(db, 'drink_recipes', recipe.id), { archived: true }); setSnackbar({ open: true, message: 'Recipe archived.', severity: 'info' }); await loadRecipes(); };
  const doDelete  = async (recipe) => { if (!recipe) return; await deleteDoc(doc(db, 'drink_recipes', recipe.id)); setSnackbar({ open: true, message: 'Recipe deleted.', severity: 'success' }); await loadRecipes(); };
  const doRestore = async (recipe) => { if (!recipe) return; await updateDoc(doc(db, 'drink_recipes', recipe.id), { archived: false }); setSnackbar({ open: true, message: 'Recipe restored.', severity: 'success' }); await loadRecipes(); };

  // filters
  const filteredRecipes =
    (categoryId) => recipes
      .filter(r =>
        r.category === categoryId &&
        (showArchived || !r.archived) &&
        (!searchTerm || r.name.toLowerCase().includes(searchTerm.toLowerCase())) &&
        (!showUnitIssuesOnly || (recipeUnitIssues[r.id]?.total > 0))
      )
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  // ---- UI: force Notes then Tags at the bottom ----
  const orderNotesTagsLast = (base) => {
    const input = Array.isArray(base) ? base.slice() : [];
    const cleaned = input.filter(k => k !== 'notes' && k !== 'tags');
    if (!cleaned.includes('*')) cleaned.push('*');
    cleaned.push('notes', 'tags'); // Notes second-to-last, Tags last
    return cleaned;
  };

  const finalUi = {
    // hide system fields
    recipe_type: { 'ui:widget': 'hidden' },
    version: { 'ui:widget': 'hidden' },
    updated_at: { 'ui:widget': 'hidden' },
    schema_version: { 'ui:widget': 'hidden' },

    // steps
    steps: {
      items: {
        applies_to_servings: { 'ui:widget': 'checkboxes' },
        ingredient_refs:    { 'ui:widget': 'checkboxes' },
        equipment_refs:     { 'ui:widget': 'checkboxes' }
      }
    },

    // servings
    servings: {
      items: {
        id: { 'ui:widget': 'hidden' },
        equipment: {
          'ui:options': { orderable: true },
          items: {
            'ui:order': ['tool_id', 'count', 'role', 'ref', '*'],
            tool_id: { 'ui:help': 'Pick from Equipment catalog (equipment_tools).' },
            ref:     { 'ui:help': 'Optional short ref, e.g., infuser, jar1' }
          }
        },
        ingredients: {
          items: {
            'ui:order': [
              'ingredient_id','qty','unit_id',
              'computed_grams','computed_oz',
              'quantity_rules','_autoGeneratedRef','reference','*'
            ],
            unit_id: { 'ui:help': 'Select a Unit from the catalog (IDs like oz, ml, g).' },
            reference: { 'ui:widget': 'ReferenceWidget' },
            computed_grams: { 'ui:disabled': true },
            computed_oz: { 'ui:disabled': true }
          }
        }
      }
    },

    // Notes as an array of textareas
    notes: {
      'ui:options': { orderable: true, addable: true, removable: true },
      items: { 'ui:widget': 'textarea' },
      'ui:help': 'Add as many notes as you need.'
    },

    // keep any caller order but ensure Notes then Tags at the end
    'ui:order': orderNotesTagsLast((uiSchemaProp && uiSchemaProp['ui:order']) || [])
  };
  const finalWidgets = { ReferenceWidget, ...widgetsProp };

  // render
  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom>Recipe Manager</Typography>

      <Button
        variant="contained"
        sx={{ mb: 2 }}
        onClick={() => {
          const base = {
            schema_version: 4,
            recipe_type: defaultRecipeType || 'beverage',
            name: '',
            category: '',
            alternative_names: [],
            supported_flavors: [],
            notes: [],     // array
            tags: [],
            servings: [
              { id: '', size: '', temperature: 'Cold', equipment: [{ tool_id: 'shaker', count: 1, role: 'mix' }], ingredients: [] }
            ],
            steps: [],
            version: 1
          };
          setEditingDocId(null);
          const initial = computeQuantities(coerceNotes(ensureAutoEquipment(pruneToSchema(base, schema))));
          setFormData(initial);
          setTab(0);
          setShowForm(true);
        }}
      >
        Add New Recipe
      </Button>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField fullWidth placeholder="Search Recipes" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} sx={{ mb: 1 }} />
        <FormControlLabel control={<Switch checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />} label="Show Archived" />
        <FormControlLabel control={<Switch checked={showUnitIssuesOnly} onChange={e => setShowUnitIssuesOnly(e.target.checked)} />} label="Only show Unit issues" />
      </Paper>

      {categories.map(category => {
        const list = filteredRecipes(category.id);
        if (!list.length) return null;
        return (
          <Accordion key={category.id} expanded={expandedCategory === category.id} onChange={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">{category.name}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {list.map(recipe => {
                const issues = recipeUnitIssues[recipe.id] || { invalid: 0, uncalibrated: 0, total: 0 };
                return (
                  <Paper key={recipe.id} sx={{ p: 2, mb: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography>{recipe.name}</Typography>
                        {issues.total > 0 && (
                          <Stack direction="row" spacing={0.5}>
                            {issues.invalid > 0 && <Chip size="small" color="error" label={`Unit invalid: ${issues.invalid}`} />}
                            {issues.uncalibrated > 0 && <Chip size="small" color="warning" label={`No calibration: ${issues.uncalibrated}`} />}
                          </Stack>
                        )}
                      </Stack>

                      <Stack direction="row" spacing={1}>
                        <Button size="small" onClick={() => {
                          const { id: _drop, ...rest } = recipe || {};
                          setEditingDocId(recipe?.id || null);
                          const pruned = pruneToSchema(rest, schema);
                          pruned.schema_version = 4; // upgrade
                          const normalized = coerceNotes(ensureAutoEquipment(pruned));
                          const { data: coerced } = coerceLegacyUnits(normalized);
                          setFormData(computeQuantities(coerced));
                          setTab(0);
                          setShowForm(true);
                        }}>
                          Edit
                        </Button>
                        {!recipe.archived ? (
                          <Button size="small" onClick={() => setArchiveTarget(recipe)}>Archive</Button>
                        ) : (
                          <>
                            <Button size="small" onClick={() => doRestore(recipe)}>Restore</Button>
                            <Button size="small" color="error" onClick={() => setDeleteTarget(recipe)}>Delete</Button>
                          </>
                        )}
                      </Stack>
                    </Box>
                  </Paper>
                );
              })}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Editor / Preview */}
      <Dialog open={showForm} onClose={() => { setShowForm(false); setEditingDocId(null); }} maxWidth="md" fullWidth>
        <DialogTitle>{editingDocId ? 'Edit Recipe' : 'Add New Recipe'}</DialogTitle>
        <DialogContent dividers>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Editor" />
            <Tab label="Crew Preview" />
          </Tabs>

          {tab === 0 && (
            <Box sx={{ '& button[type="submit"]': { display: 'none !important' } }}>
              {schemaReady && (
                <Form
                  key={`form-${sizing.length}-${ingredients.length}-${flavors.length}-${units.length}-${tools.length}-${formData?.servings?.length || 0}`}
                  schema={formSchema}
                  uiSchema={finalUi}
                  formData={formData}
                  formContext={{ formData }}
                  widgets={finalWidgets}
                  onChange={({ formData }) => {
                    const pruned = pruneToSchema(formData, schema);
                    pruned.schema_version = 4;
                    const withRefs = coerceNotes(ensureAutoEquipment(pruned));
                    const withQuant = computeQuantities(withRefs);
                    setFormData(withQuant);
                  }}
                  onSubmit={({ formData }) => handleSubmit(formData)}
                  validator={validator}
                  templates={{ ButtonBar: () => null }}
                />
              )}
            </Box>
          )}

          {tab === 1 && (
            <Box>
              {!formData?.servings?.length ? (
                <Typography variant="body2" color="text.secondary">Add at least one serving and step to preview crew instructions.</Typography>
              ) : (
                <Box>
                  {Object.entries(resolveCrewLines(formData)).map(([servId, lines]) => (
                    <Paper key={servId} variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>{servId.toUpperCase()}</Typography>
                      {lines.length ? (
                        <Stack spacing={0.5}>{lines.map((ln, i) => (<Typography key={i} variant="body2">• {ln}</Typography>))}</Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">No applicable steps.</Typography>
                      )}
                    </Paper>
                  ))}
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary">Preview resolves refs per serving using current form data. Equipment mentions derive from that serving’s equipment list.</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowForm(false); setEditingDocId(null); }}>Close</Button>
          {tab === 0 && (
            <Button variant="contained" onClick={() => document.querySelector('form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))}>
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Archive/Delete */}
      <Dialog open={!!archiveTarget} onClose={() => setArchiveTarget(null)}>
        <DialogTitle>Archive this recipe?</DialogTitle>
        <DialogContent dividers><Typography>{archiveTarget ? `“${archiveTarget.name}” will be hidden from active lists. You can restore it later.` : ''}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={async () => { const target = archiveTarget; setArchiveTarget(null); await doArchive(target); }}>Archive</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete this recipe?</DialogTitle>
        <DialogContent dividers><Typography color="error">{deleteTarget ? `“${deleteTarget.name}” will be permanently deleted. This action cannot be undone.` : ''}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={async () => { const target = deleteTarget; setDeleteTarget(null); await doDelete(target); }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
